import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, Instagram, MapPin, UserRound, ShoppingBag } from "lucide-react";
import { SERVICES, SOCIALS } from "../data/services";

const EXPLORE = [
  { label: "Home", to: "/" },
  { label: "SEC Studios", to: "/studios" },
  { label: "South East Collectibles", to: "/collectibles" },
  { label: "Shop", to: "/shop" },
  { label: "Consignments", to: "/consignments" },
  { label: "Book Studio", to: "/book" },
  { label: "My Account", to: "/account" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const ease = [0.22, 1, 0.36, 1];

  return (
    <>
      <motion.header
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.7, ease }}
        className={`fixed top-0 left-0 right-0 z-[60] transition-colors duration-500 ${
          scrolled || open
            ? "bg-black/85 backdrop-blur-xl border-b border-white/10"
            : "bg-transparent"
        }`}
        data-testid="navbar"
      >
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
            <span className="h-9 w-9 grid place-items-center border border-gold text-gold font-display font-semibold text-lg group-hover:bg-gold group-hover:text-black transition-colors duration-300">
              S
            </span>
            <span className="font-display font-semibold tracking-tight text-sm sm:text-base leading-none">
              SOUTH EAST
              <span className="block text-gold text-[10px] font-mono tracking-[0.25em] mt-0.5">
                COLLECTIBLES
              </span>
            </span>
          </Link>

          <div className="flex items-center gap-3">
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 rounded-full bg-white text-black font-mono text-xs uppercase tracking-[0.14em] px-5 py-2.5 font-bold hover:bg-gold transition-colors duration-300"
              data-testid="nav-shop-cta"
            >
              <ShoppingBag size={15} /> Shop
            </Link>
            <Link
              to="/account"
              className="h-10 w-10 grid place-items-center rounded-full border border-white/25 hover:border-gold hover:text-gold text-white transition-colors duration-300"
              data-testid="nav-account"
              aria-label="My account"
            >
              <UserRound size={16} />
            </Link>
            <Link
              to="/book"
              className="hidden sm:inline-flex rounded-full bg-gold text-black font-mono text-xs uppercase tracking-[0.12em] px-5 py-2.5 font-semibold hover:bg-[#d59e19] transition-colors duration-300"
              data-testid="nav-book-cta"
            >
              Book Studio
            </Link>
            <button
              onClick={() => setOpen((v) => !v)}
              className="inline-flex items-center gap-2 rounded-full border border-white/25 hover:border-gold hover:text-gold text-white font-mono text-xs uppercase tracking-[0.12em] px-5 py-2.5 transition-colors duration-300"
              data-testid="nav-menu-toggle"
            >
              {open ? <X size={16} /> : <Menu size={16} />}
              {open ? "Close" : "Menu"}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[55] bg-black/60 backdrop-blur-sm"
              onClick={() => setOpen(false)}
            />
            <motion.nav
              initial={{ y: "-100%" }}
              animate={{ y: 0 }}
              exit={{ y: "-100%" }}
              transition={{ duration: 0.5, ease }}
              className="fixed top-[72px] left-0 right-0 z-[58] bg-[#0b0b0b] border-b border-white/10 max-h-[calc(100vh-72px)] overflow-y-auto"
              data-testid="nav-dropdown"
            >
              <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-10 sm:py-14 grid lg:grid-cols-12 gap-10">
                <div className="lg:col-span-5">
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold">
                    [ Explore ]
                  </span>
                  <ul className="mt-6 space-y-1">
                    {EXPLORE.map((n, i) => (
                      <motion.li
                        key={n.to}
                        initial={{ opacity: 0, x: -16 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.08 + i * 0.05 }}
                      >
                        <Link
                          to={n.to}
                          className="group flex items-center justify-between py-1.5"
                          data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          <span className={`font-display text-3xl sm:text-4xl font-semibold uppercase tracking-tight transition-colors ${pathname === n.to ? "text-gold" : "text-white group-hover:text-gold"}`}>
                            {n.label}
                          </span>
                          <ArrowUpRight className="text-zinc-600 group-hover:text-gold transition-colors" size={22} />
                        </Link>
                      </motion.li>
                    ))}
                  </ul>
                </div>

                <div className="lg:col-span-4">
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold">
                    [ Services ]
                  </span>
                  <ul className="mt-6 grid sm:grid-cols-1 gap-y-2">
                    {SERVICES.map((s, i) => (
                      <motion.li
                        key={s.slug}
                        initial={{ opacity: 0, x: -12 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.15 + i * 0.03 }}
                      >
                        <Link
                          to={`/services/${s.slug}`}
                          className="group flex items-center gap-3 py-1 text-zinc-300 hover:text-white"
                          data-testid={`nav-service-${s.slug}`}
                        >
                          <span className="font-mono text-[10px] text-zinc-600 group-hover:text-gold">{s.number}</span>
                          <span className="text-sm">{s.title}</span>
                        </Link>
                      </motion.li>
                    ))}
                  </ul>
                </div>

                <div className="lg:col-span-3">
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold">
                    [ Find Us ]
                  </span>
                  <div className="mt-6 space-y-4 text-zinc-300 text-sm">
                    <p className="inline-flex items-start gap-2">
                      <MapPin size={16} className="mt-0.5 shrink-0 text-gold" /> {SOCIALS.location}
                    </p>
                    <a href={`https://instagram.com/${SOCIALS.studios.replace("@", "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-white">
                      <Instagram size={16} className="text-gold" /> {SOCIALS.studios}
                    </a>
                    <a href={`https://instagram.com/${SOCIALS.collectibles.replace("@", "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-white">
                      <Instagram size={16} className="text-gold" /> {SOCIALS.collectibles}
                    </a>
                    <Link to="/book" className="inline-flex mt-2 rounded-full bg-gold text-black font-mono text-xs uppercase tracking-[0.12em] px-5 py-3 font-semibold hover:bg-[#d59e19] transition-colors">
                      Book The Studio
                    </Link>
                  </div>
                </div>
              </div>
            </motion.nav>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
